// Built-in, per-language defaults for the article rating block. Mirror of the
// `articleRatingTitle` / `articleNoHelpRatingTitle` / `articleHelpButton` keys
// in HelpCenterApp/helper/translations.ts. Keyed by the 2-letter language code.
// A custom wording set in the dashboard (Help center -> Translations -> Article
// rating) lives in helpcenterConfig.staticTranslations[lang] and takes
// precedence over these; this table is the fallback when no override exists.

export interface ArticleRatingStrings {
  articleRatingTitle: string;
  articleNoHelpRatingTitle: string;
  articleHelpButton: string;
}

export const ARTICLE_RATING_DEFAULTS: Record<string, ArticleRatingStrings> = {
  en: {
    articleRatingTitle: "Did this answer your question?",
    articleNoHelpRatingTitle:
      "Sorry that you didn't find what you were looking for. If you like, you can reach out for additional help here.",
    articleHelpButton: "Chat with us",
  },
  de: {
    articleRatingTitle: "War diese Antwort hilfreich für dich?",
    articleNoHelpRatingTitle:
      "Entschuldige, dass du nicht das gefunden hast, wonach du gesucht hast. Unser Team hilft dir jedoch sehr gerne!",
    articleHelpButton: "Schreib uns eine Nachricht",
  },
  es: {
    articleRatingTitle: "¿Te ha ayudado esta respuesta?",
    articleNoHelpRatingTitle:
      "Lo sentimos, no hemos encontrado lo que estabas buscando. Si lo deseas, puedes ponerte en contacto con nosotros para obtener ayuda adicional.",
    articleHelpButton: "Escríbenos un mensaje",
  },
  fr: {
    articleRatingTitle: "Cette réponse vous a-t-elle aidé ?",
    articleNoHelpRatingTitle:
      "Désolé, nous n'avons pas trouvé ce que vous cherchiez. Si vous le souhaitez, vous pouvez nous contacter pour obtenir de l'aide supplémentaire.",
    articleHelpButton: "Écrivez-nous un message",
  },
  pt: {
    articleRatingTitle: "Esta resposta foi útil?",
    articleNoHelpRatingTitle:
      "Desculpe, não encontramos o que você estava procurando. Se desejar, você pode entrar em contato conosco para obter ajuda adicional.",
    articleHelpButton: "Envie-nos uma mensagem",
  },
  az: {
    articleRatingTitle: "Bu sualınıza cavab verdi?",
    articleNoHelpRatingTitle:
      "Təəssüf ki, axtardığınızı tapa bilmədiniz. İstəsəniz, əlavə kömək üçün buradan əlaqə saxlaya bilərsiniz.",
    articleHelpButton: "Bizimlə söhbət edin",
  },
  zh: {
    articleRatingTitle: "这个回答对您有帮助吗？",
    articleNoHelpRatingTitle:
      "抱歉，我们未能找到您想要的内容。如果您愿意，可以联系我们获取额外的帮助。",
    articleHelpButton: "与我们聊天",
  },
  nl: {
    articleRatingTitle: "Heeft dit antwoord je vraag beantwoord?",
    articleNoHelpRatingTitle:
      "Sorry, we hebben niet gevonden wat je zocht. Als je wilt, kun je contact met ons opnemen voor extra hulp.",
    articleHelpButton: "Stuur ons een bericht",
  },
  cs: {
    articleRatingTitle: "Tato odpověď odpovídá vašemu dotazu?",
    articleNoHelpRatingTitle:
      "Omlouváme se, ale nenašli jsme to, co jste hledali. Pokud chcete, můžete nás kontaktovat pro další pomoc.",
    articleHelpButton: "Napište nám zprávu",
  },
  it: {
    articleRatingTitle: "Questa risposta ti è stata utile?",
    articleNoHelpRatingTitle:
      "Spiacenti, non abbiamo trovato ciò che stavi cercando. Se lo desideri, puoi contattarci per ulteriore assistenza.",
    articleHelpButton: "Scrivici un messaggio",
  },
  ru: {
    articleRatingTitle: "Был ли этот ответ полезен?",
    articleNoHelpRatingTitle:
      "Извините, мы не нашли то, что вы искали. Если хотите, вы можете связаться с нами для получения дополнительной помощи.",
    articleHelpButton: "Напишите нам",
  },
  uk: {
    articleRatingTitle: "Чи була ця відповідь корисною?",
    articleNoHelpRatingTitle:
      "Вибачте, ми не знайшли те, що ви шукали. Якщо ви хочете, ви можете зв'язатися з нами для отримання додаткової допомоги.",
    articleHelpButton: "Напишіть нам",
  },
  tr: {
    articleRatingTitle: "Bu cevap sorunuza yardımcı oldu mu?",
    articleNoHelpRatingTitle:
      "Üzgünüz, aradığınızı bulamadık. İsterseniz ek yardım için bizimle iletişime geçebilirsiniz.",
    articleHelpButton: "Bize mesaj gönderin",
  },
  pl: {
    articleRatingTitle: "Czy ta odpowiedź pomogła?",
    articleNoHelpRatingTitle:
      "Przepraszamy, nie znaleźliśmy tego, czego szukasz. Jeśli chcesz, możesz skontaktować się z nami, aby uzyskać dodatkową pomoc.",
    articleHelpButton: "Napisz do nas",
  },
  da: {
    articleRatingTitle: "Var dette svar nyttigt?",
    articleNoHelpRatingTitle:
      "Beklager, vi fandt ikke, hvad du ledte efter. Hvis du vil, kan du kontakte os for yderligere hjælp.",
    articleHelpButton: "Skriv til os",
  },
  sv: {
    articleRatingTitle: "Hjälpte detta svar dig?",
    articleNoHelpRatingTitle:
      "Ledsen att du inte hittade vad du letade efter. Om du vill kan du kontakta oss för ytterligare hjälp.",
    articleHelpButton: "Chatta med oss",
  },
  fi: {
    articleRatingTitle: "Oliko tästä vastauksesta apua?",
    articleNoHelpRatingTitle:
      "Pahoittelut, emme löytäneet etsimääsi. Voit halutessasi ottaa meihin yhteyttä saadaksesi lisäapua.",
    articleHelpButton: "Lähetä meille viesti",
  },
  id: {
    articleRatingTitle: "Apakah jawaban ini membantu?",
    articleNoHelpRatingTitle:
      "Maaf, kami tidak menemukan yang Anda cari. Jika Anda mau, Anda bisa menghubungi kami untuk bantuan tambahan.",
    articleHelpButton: "Kirim pesan kepada kami",
  },
  no: {
    articleRatingTitle: "Var dette svaret til hjelp?",
    articleNoHelpRatingTitle:
      "Beklager, vi fant ikke det du lette etter. Hvis du vil, kan du kontakte oss for ytterligere hjelp.",
    articleHelpButton: "Send oss en melding",
  },
  nb: {
    articleRatingTitle: "Var dette svaret til hjelp?",
    articleNoHelpRatingTitle:
      "Beklager, vi fant ikke det du lette etter. Hvis du vil, kan du kontakte oss for ytterligere hjelp.",
    articleHelpButton: "Send oss en melding",
  },
  ko: {
    articleRatingTitle: "이 답변이 도움이 되었나요?",
    articleNoHelpRatingTitle:
      "죄송합니다. 찾으시는 내용을 찾을 수 없었습니다. 원하신다면 추가 도움을 받기 위해 연락할 수 있습니다.",
    articleHelpButton: "문의하기",
  },
  ar: {
    articleRatingTitle: "هل كانت هذه الإجابة مفيدة؟",
    articleNoHelpRatingTitle:
      "عذرًا، لم نجد ما كنت تبحث عنه. إذا كنت ترغب، يمكنك التواصل معنا للحصول على مساعدة إضافية.",
    articleHelpButton: "أرسل لنا رسالة",
  },
  he: {
    articleRatingTitle: "האם התשובה עזרה לך?",
    articleNoHelpRatingTitle:
      "מצטערים, לא מצאנו את מה שחיפשת. אם תרצה, תוכל ליצור איתנו קשר לקבלת עזרה נוספת.",
    articleHelpButton: "שלח לנו הודעה",
  },
  hu: {
    articleRatingTitle: "Hasznos volt a válasz?",
    articleNoHelpRatingTitle:
      "Sajnáljuk, nem találtuk, amit keresett. Ha szeretné, forduljon hozzánk további segítségért.",
    articleHelpButton: "Írjon nekünk",
  },
  ro: {
    articleRatingTitle: "A fost util acest răspuns?",
    articleNoHelpRatingTitle:
      "Ne pare rău, nu am găsit ce căutai. Dacă dorești, poți să ne contactezi pentru ajutor suplimentar.",
    articleHelpButton: "Trimite-ne un mesaj",
  },
  bg: {
    articleRatingTitle: "Помогна ли този отговор?",
    articleNoHelpRatingTitle:
      "Съжаляваме, но не намерихме това, което търсехте. Ако желаете, можете да се свържете с нас за допълнителна помощ.",
    articleHelpButton: "Изпратете ни съобщение",
  },
  sk: {
    articleRatingTitle: "Pomohla vám táto odpoveď?",
    articleNoHelpRatingTitle:
      "Prepáčte, ale nenašli sme to, čo ste hľadali. Ak chcete, môžete nás kontaktovať pre ďalšiu pomoc.",
    articleHelpButton: "Napíšte nám",
  },
  sl: {
    articleRatingTitle: "Je bil ta odgovor uporaben?",
    articleNoHelpRatingTitle:
      "Oprostite, nismo našli, kar ste iskali. Če želite, nas lahko kontaktirate za dodatno pomoč.",
    articleHelpButton: "Pošlji nam sporočilo",
  },
  ms: {
    articleRatingTitle: "Adakah jawapan ini membantu anda?",
    articleNoHelpRatingTitle:
      "Maaf kerana tidak menemui apa yang anda cari. Jika anda mahu, anda boleh hubungi kami untuk bantuan tambahan.",
    articleHelpButton: "Chat dengan kami",
  },
  ja: {
    articleRatingTitle: "この回答が役に立ちましたか？",
    articleNoHelpRatingTitle:
      "申し訳ありませんが、お探しのものが見つかりませんでした。必要な場合は、追加の支援を受けるためにお問い合わせください。",
    articleHelpButton: "お問い合わせ",
  },
  el: {
    articleRatingTitle: "Υπήρξε χρήσιμη αυτή η απάντηση;",
    articleNoHelpRatingTitle:
      "Λυπούμαστε, δεν βρήκαμε το που ψάχνετε. Αν θέλετε, μπορείτε να επικοινωνήσετε μαζί μας για περισσότερη βοήθεια.",
    articleHelpButton: "Στείλτε μας μήνυμα",
  },
  vi: {
    articleRatingTitle: "Câu trả lời này có giải đáp được câu hỏi của bạn không?",
    articleNoHelpRatingTitle:
      "Rất tiếc vì bạn chưa tìm thấy điều mình cần. Nếu muốn, bạn có thể liên hệ để được hỗ trợ thêm tại đây.",
    articleHelpButton: "Trò chuyện với chúng tôi",
  },
};
